import type { LangCode, User } from "@scoach/types";
import { create } from "zustand";
import { persist, subscribeWithSelector } from "zustand/middleware";

import { onAuthChange } from "../../lib/firebase.ts";

export interface AuthState {
  /** Bootstrapping flag — true once we've checked Firebase / hydrated from storage. */
  initialized: boolean;
  /** Firebase user info we care about. Persisted so reloads don't sign-out. */
  firebaseUid: string | null;
  email: string | null;
  displayName: string | null;
  /** Server profile (from /users/me). NOT persisted — refetched on reload. */
  user: User | null;
  /** User's Gemini API key (browser-local; only used for Quiet Ask). Persisted. */
  geminiKey: string | null;
  companyRoles: string[];
  /** Google OAuth access token for Calendar API. Persisted. */
  googleAccessToken: string | null;
  /** Google OAuth access token with bigquery + userinfo.email scopes, captured at
   * sign-in and reused for the Company Report (Billing/Growth) so the user never
   * has to re-connect. Persisted (survives reload); ~1h lifetime. */
  bqAccessToken: string | null;
  /** Epoch ms when bqAccessToken expires (best-effort ~55m after capture). Persisted. */
  bqTokenExp: number | null;
  /** Preferred language for simulations (Gemini speaks it). Persisted. */
  simulationLanguage: LangCode;
  /** Whether the user has been shown the "how to use Sally" onboarding video. Persisted. */
  onboardingVideoSeen: boolean;
  /** Whether the user hid the floating onboarding-agent bubble. When true, the bubble
   * isn't shown; the guide is still reachable from the header menu. Persisted. */
  onboardingBubbleHidden: boolean;

  setFirebaseUser: (uid: string | null, email: string | null, displayName?: string | null) => void;
  setUser: (user: User | null) => void;
  setGeminiKey: (key: string | null) => void;
  setCompanyRoles: (roles: string[]) => void;
  setGoogleAccessToken: (token: string | null) => void;
  /** Store (or clear) the BigQuery access token. `expMs` is the epoch-ms expiry. */
  setBqToken: (token: string | null, expMs?: number | null) => void;
  setSimulationLanguage: (lang: LangCode) => void;
  setOnboardingVideoSeen: (seen: boolean) => void;
  setOnboardingBubbleHidden: (hidden: boolean) => void;
  clear: () => void;
  /** Mark bootstrap done (after hydrate or onAuthChange first-fire). */
  markInitialized: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    subscribeWithSelector((set) => ({
      initialized: false,
      firebaseUid: null,
      email: null,
      displayName: null,
      user: null,
      geminiKey: "AQ.Ab8RN6I-snWrf28VwiE49bdsEntFNwAvDAgWt2DByuIXwNnyrw",
      companyRoles: [],
      googleAccessToken: null,
      bqAccessToken: null,
      bqTokenExp: null,
      simulationLanguage: "en",
      onboardingVideoSeen: false,
      onboardingBubbleHidden: false,

      setFirebaseUser: (uid, email, displayName) =>
        set({ firebaseUid: uid, email, displayName: displayName ?? null, initialized: true }),
      setUser: (user) => set({ user }),
      setGeminiKey: (key) => set({ geminiKey: key }),
      setCompanyRoles: (roles) => set({ companyRoles: roles }),
      setGoogleAccessToken: (token) => set({ googleAccessToken: token }),
      setBqToken: (token, expMs) =>
        set({ bqAccessToken: token, bqTokenExp: token ? (expMs ?? null) : null }),
      setSimulationLanguage: (lang) => set({ simulationLanguage: lang }),
      setOnboardingVideoSeen: (seen) => set({ onboardingVideoSeen: seen }),
      setOnboardingBubbleHidden: (hidden) => set({ onboardingBubbleHidden: hidden }),
      // NOTE: deliberately does NOT wipe bqAccessToken/bqTokenExp. clear() also runs on
      // a transient /users/me 401 (see routes/_app.tsx); wiping the BigQuery token there
      // was making the Company Report re-prompt for Google mid-session. The token is
      // short-lived and re-minted silently via GIS anyway, so keeping it is safe.
      clear: () =>
        set({
          firebaseUid: null,
          email: null,
          displayName: null,
          user: null,
          googleAccessToken: null,
          initialized: true,
        }),
      markInitialized: () => set({ initialized: true }),
    })),
    {
      name: "scoach.auth",
      // Only persist the bits we want to survive a page reload.
      partialize: (s) => ({
        firebaseUid: s.firebaseUid,
        email: s.email,
        displayName: s.displayName,
        geminiKey: s.geminiKey,
        companyRoles: s.companyRoles,
        googleAccessToken: s.googleAccessToken,
        bqAccessToken: s.bqAccessToken,
        bqTokenExp: s.bqTokenExp,
        simulationLanguage: s.simulationLanguage,
        onboardingVideoSeen: s.onboardingVideoSeen,
        onboardingBubbleHidden: s.onboardingBubbleHidden,
      }),
    },
  ),
);

/**
 * Wires up Firebase auth state subscription. Call once at app bootstrap.
 *
 * Behavior:
 * - If Firebase IS configured (env vars set), trust Firebase's onAuthStateChanged
 *   as the source of truth — overwrite the persisted store with whatever Firebase
 *   reports.
 * - If Firebase is NOT configured (dev with no .env.local), keep whatever was
 *   persisted to localStorage and just mark initialized=true.
 */
export function bootstrapAuth(): () => void {
  try {
    return onAuthChange((u) => {
      useAuthStore
        .getState()
        .setFirebaseUser(u?.uid ?? null, u?.email ?? null, u?.displayName ?? null);
    });
  } catch {
    // No Firebase config available — keep whatever's persisted, just mark
    // initialized so the route guards proceed.
    useAuthStore.getState().markInitialized();
    return () => {};
  }
}
